import { usePreviewFormContext } from './PreviewFormContext';
import React, { useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import type { FormAppearance } from '@eaco/forms-core';
import { cn } from '../lib/utils';
import { useFormFillContext } from './FormFillContext';

/**
 * Form-level brand band shown at the top of a fill / preview: the owner's
 * logo, the form name, and (optional) the owner org name, on the Appearance
 * "header background" treatment. Reuses the same light, cache-first data
 * path as the Logo element (`ownerName` / `ownerLogo`, resolved server-side:
 * PDF logo → square avatar → null), extended with the form `name` +
 * `appearance`. Works in the Design canvas, Preview and real fills.
 *
 * The host decides placement / sticky behaviour; this renders the band only.
 */
const BRAND_QUERY = gql`
  query formBrandHeader($id: ID!) {
    formDocument(id: $id) {
      id
      name
      ownerName
      ownerLogo {
        id
        url
      }
      appearance
    }
  }
`;

const PUBLIC_BRAND_QUERY = gql`
  query publicFormBrandHeader($publicSlug: String!) {
    publicForm(publicSlug: $publicSlug) {
      publicSlug
      name
      ownerName
      ownerLogo {
        id
        url
      }
    }
  }
`;

const TINT = 'var(--forms-accent,var(--forms-accent-default))';
const TINT_BAND = 'color-mix(in srgb, var(--forms-accent,var(--forms-accent-default)) 8%, #fff)';
const TINT_BORDER = 'color-mix(in srgb, var(--forms-accent,var(--forms-accent-default)) 18%, #fff)';

interface Props {
  /** Live appearance override (e.g. the editor's unsaved settings). */
  appearance?: FormAppearance | null;
  /** Live form-name override (e.g. the editor's unsaved name). */
  formName?: string;
  className?: string;
  /**
   * "band" (default) — the horizontal top band used above the form on
   * narrow layouts. "rail" — the compact tile shown at the top of the
   * desktop side-stepper rail (logo tile + stacked name/org). The rail
   * variant ignores the Appearance header treatment (it always sits on the
   * rail's own light surface).
   */
  variant?: 'band' | 'rail';
}

const FormBrandHeader: React.FC<Props> = ({
  appearance,
  formName,
  className,
  variant = 'band',
}) => {
  const [imgFailed, setImgFailed] = useState(false);
  const { formOid, publicSlug } = useFormFillContext();
  // read before any early return so the hook order never changes
  const bandStatus = usePreviewFormContext()?.statusSlot ?? null;
  const { data: authData } = useQuery(BRAND_QUERY, {
    variables: { id: formOid ?? '' },
    skip: !formOid,
    fetchPolicy: 'cache-first',
  });
  const { data: publicData } = useQuery(PUBLIC_BRAND_QUERY, {
    variables: { publicSlug: publicSlug ?? '' },
    skip: !!formOid || !publicSlug,
    fetchPolicy: 'cache-first',
  });
  const owner = formOid ? authData?.formDocument : publicData?.publicForm;

  const name = formName || owner?.name || '';
  const orgName: string = owner?.ownerName || '';
  const logoUrl: string | null = owner?.ownerLogo?.url ?? null;

  // Appearance: caller override wins; else the queried form appearance
  // (public forms don't expose it → default treatment).
  const ap: FormAppearance | null =
    appearance ??
    (owner && 'appearance' in owner
      ? ((owner as { appearance?: FormAppearance }).appearance ?? null)
      : null);
  const showLogo = ap?.header?.showLogo !== false; // default on
  const bg = ap?.header?.background ?? 'tint';
  const onSolid = bg === 'solid';

  if (!name && !orgName && !logoUrl) {
    // no branding to show (or not loaded): the status still has to be seen
    if (variant === 'band' && bandStatus) {
      return (
        <div className={cn('flex items-center justify-end border-b border-[#edf0f4] bg-white px-[18px] py-[10px]', className)}>
          {bandStatus}
        </div>
      );
    }
    return null;
  }

  // Rail variant: compact tile + stacked name/org at the top of the desktop
  // side-stepper rail. Fixed light surface, own divider — no Appearance
  // header treatment (that's a band-only concern).
  if (variant === 'rail') {
    return (
      <div
        className={cn(
          // Fixed height (shared with the answer-column header in
          // FillDesktopFrame) so the rail brand and the STEP header share one
          // continuous bottom divider across the card.
          'flex h-[74px] items-center gap-[10px] border-b border-[#edf0f4] px-[18px]',
          className,
        )}
      >
        {showLogo &&
          (logoUrl && !imgFailed ? (
            <img
              src={logoUrl}
              alt={`${orgName || name} logo`}
              onError={() => setImgFailed(true)}
              className="h-[34px] w-auto max-w-[120px] shrink-0 object-contain"
            />
          ) : (
            <span
              className="inline-flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[9px] text-[15px] font-extrabold text-white shadow-[0_2px_5px_rgba(47,111,219,.32)]"
              style={{ backgroundColor: TINT }}
            >
              {(orgName || name || '·').charAt(0).toUpperCase()}
            </span>
          ))}
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13.5px] font-extrabold tracking-[-0.01em] text-[#1f2a3d]">
            {name}
          </div>
          {orgName && (
            <div className="truncate text-[11px] font-semibold text-[#6b7c9a]">
              {orgName}
            </div>
          )}
        </div>
      </div>
    );
  }

  const bandStyle: React.CSSProperties =
    bg === 'solid'
      ? { backgroundColor: TINT, borderColor: 'transparent' }
      : bg === 'plain'
        ? { backgroundColor: '#fff', borderColor: '#edf0f4' }
        : { backgroundColor: TINT_BAND, borderColor: TINT_BORDER };

  return (
    <div
      className={cn(
        // the status wraps under the name when a logo and a score leave no room beside it
        'flex flex-wrap items-center gap-x-[11px] gap-y-[8px] border-b px-[18px] py-[13px]',
        className,
      )}
      style={bandStyle}
    >
      {showLogo &&
        (logoUrl && !imgFailed ? (
          <img
            src={logoUrl}
            alt={`${orgName || name} logo`}
            onError={() => setImgFailed(true)}
            className="h-8 w-auto max-w-[130px] shrink-0 object-contain"
          />
        ) : (
          <span
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] text-[15px] font-extrabold text-white"
            style={{ backgroundColor: onSolid ? 'rgba(255,255,255,.22)' : TINT }}
          >
            {(orgName || name || '·').charAt(0).toUpperCase()}
          </span>
        ))}
      <div className="min-w-0 flex-1 basis-[140px]">
        <div
          className="truncate text-[14px] font-extrabold tracking-[-0.01em]"
          style={{ color: onSolid ? '#fff' : '#1f2a3d' }}
        >
          {name}
        </div>
        {orgName && (
          <div
            className="truncate text-[11px] font-semibold"
            style={{ color: onSolid ? 'rgba(255,255,255,.85)' : '#6b7c9a' }}
          >
            {orgName}
          </div>
        )}
      </div>
      {/* The host's status (autosave, live score) rides at the band's end on
          the narrow layout, where there is no answer-column header for it. */}
      {bandStatus && <div className="ml-auto flex-none">{bandStatus}</div>}
    </div>
  );
};

export default FormBrandHeader;
